/**
 * YunExpressSection — org-wide YunExpress (China shipping) account.
 *
 * Mirrors `StarShipItSection` / `PortlessSection`: a green "saved" or amber
 * "not set up" status block with Replace / Add, a write-only credential
 * editor, and Test connection. Saving goes through the Console's sticky save
 * bar only (`useDirtyRegistration`); there is no inline Save button.
 *
 * Credentials are three Vault values (App ID, App Secret, Source Key). The
 * backend only reports whether a COMPLETE set is saved, so "not set up" covers
 * both none and partial. With nothing saved, all three are required before
 * Save (the backend refuses a partial first save with
 * `YUNEXPRESS_CREDENTIALS_INCOMPLETE`). Once a complete set exists, a blank
 * field keeps its saved value, so one key can be rotated on its own.
 *
 * The environment line says which YunExpress host this server talks to
 * (`getYunExpressSettings().apiEnvironment`). The host is a server setting,
 * not an operator one, so the line explains and never offers an edit.
 *
 * Focus follows the editor: Add / Replace moves it to App ID (the button that
 * had it unmounts), and Cancel returns it to the Add / Replace button, so a
 * keyboard user is never dropped to the top of the page. A save or a discard
 * from the sticky bar leaves focus where it is, on the bar.
 *
 * Ningbo admins read zh, so all copy is in the `settings` namespace.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, KeyRound, Loader2, RefreshCw, Send, ShieldAlert, ShieldCheck, Truck, X } from 'lucide-react';
import { toast } from 'sonner';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { noAutofillProps } from '@/components/ui/no-autofill';
import { SecretInput } from '@/components/ui/secret-input';
import { Button } from '@/components/ui/button';
import { trpc } from '@/lib/trpc';
import { readCauseCode } from '@/lib/trpc-error-utils';
import { cn } from '@/lib/utils';
import { SettingsSection } from '../components/console/SettingsSection';
import { useDirtyRegistration } from '../components/console/SettingsDirtyContext';

const ENVIRONMENT_COPY = {
  production: { key: 'production.yunexpress.envProduction', tone: 'text-muted-foreground' },
  sandbox: { key: 'production.yunexpress.envSandbox', tone: 'text-amber-700' },
  invalid: { key: 'production.yunexpress.envInvalid', tone: 'text-destructive' },
} as const;

export function YunExpressSection() {
  const { t } = useTranslation('settings');
  const settingsQuery = trpc.productionSettings.getYunExpressSettings.useQuery();
  const utils = trpc.useUtils();
  const updateMutation = trpc.productionSettings.updateYunExpressSettings.useMutation();
  const testMutation = trpc.productionSettings.testYunExpress.useMutation();

  const [appId, setAppId] = useState('');
  const [appSecret, setAppSecret] = useState('');
  const [sourceKey, setSourceKey] = useState('');
  const [isEditingCredentials, setIsEditingCredentials] = useState(false);
  const appIdRef = useRef<HTMLInputElement>(null);
  const editorToggleRef = useRef<HTMLButtonElement>(null);
  // Where focus goes on the next render that flips the editor. Set only by an
  // explicit Add / Replace / Cancel click, never by a save-bar reset.
  const pendingFocusRef = useRef<'appId' | 'toggle' | null>(null);

  useEffect(() => {
    const target = pendingFocusRef.current;
    if (!target) return;
    pendingFocusRef.current = null;
    (target === 'appId' ? appIdRef.current : editorToggleRef.current)?.focus();
  }, [isEditingCredentials]);

  const openEditor = () => {
    pendingFocusRef.current = 'appId';
    setIsEditingCredentials(true);
  };

  const hasCredentials = settingsQuery.data?.hasCredentials ?? false;
  const apiEnvironment = settingsQuery.data?.apiEnvironment;
  const environment = apiEnvironment ? ENVIRONMENT_COPY[apiEnvironment] : undefined;

  const draft = { appId: appId.trim(), appSecret: appSecret.trim(), sourceKey: sourceKey.trim() };
  const filledCount = [draft.appId, draft.appSecret, draft.sourceKey].filter(Boolean).length;
  const isDirty = filledCount > 0;
  // Nothing complete is saved yet: a partial set would leave YunExpress
  // half-configured, so all three are required.
  const incompleteFirstSave = !hasCredentials && isDirty && filledCount < 3;

  const resetDraft = () => {
    setAppId('');
    setAppSecret('');
    setSourceKey('');
    setIsEditingCredentials(false);
  };

  const cancelEditor = () => {
    pendingFocusRef.current = 'toggle';
    resetDraft();
  };

  useDirtyRegistration(
    'yunexpress',
    isDirty
      ? {
          label: t('production.yunexpress.dirtyLabel'),
          save: async () => {
            if (incompleteFirstSave) {
              const message = t('production.yunexpress.enterAllThree');
              toast.error(message);
              throw new Error(message);
            }
            try {
              await updateMutation.mutateAsync({
                appId: draft.appId || undefined,
                appSecret: draft.appSecret || undefined,
                sourceKey: draft.sourceKey || undefined,
              });
            } catch (error) {
              toast.error(
                readCauseCode(error) === 'YUNEXPRESS_CREDENTIALS_INCOMPLETE'
                  ? t('production.yunexpress.enterAllThree')
                  : t('production.yunexpress.saveError', {
                      message: error instanceof Error ? error.message : String(error),
                    }),
              );
              throw error;
            }
            resetDraft();
            await Promise.all([
              settingsQuery.refetch(),
              utils.productionSettings.getYunExpressShippingMethods.invalidate(),
              utils.shipping.getShippingSettings.invalidate(),
            ]);
          },
          discard: resetDraft,
        }
      : null,
  );

  const handleTest = () => {
    testMutation.mutate(undefined, {
      onSuccess: (result) => {
        if (result.success) {
          toast.success(t('production.yunexpress.testSuccess'));
        } else {
          toast.error(t('production.yunexpress.testFailed'), {
            description: result.error || undefined,
          });
        }
      },
      onError: (error) => toast.error(t('production.yunexpress.testFailed'), { description: error.message }),
    });
  };

  const editorHintId = 'yunexpress-credential-editor-hint';

  return (
    <SettingsSection
      id="yunexpress"
      title="YunExpress"
      description={t('production.yunexpress.description')}
      icon={Truck}
      scope="org"
    >
      {settingsQuery.isPending ? (
        <div className="flex items-center justify-center py-8" role="status" aria-busy="true">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden="true" />
          <span className="sr-only">{t('production.yunexpress.loading')}</span>
        </div>
      ) : settingsQuery.isError ? (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
          <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600" aria-hidden="true" />
          <p className="min-w-0 flex-1 text-sm text-amber-800">{t('production.yunexpress.loadError')}</p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-10 gap-2 border-amber-300 bg-white/70 text-amber-900 hover:bg-white"
            onClick={() => void settingsQuery.refetch()}
          >
            <RefreshCw className="h-4 w-4" aria-hidden="true" />
            {t('production.common.retry')}
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {hasCredentials ? (
            <div className="flex flex-wrap items-center gap-3 rounded-lg border border-green-200 bg-green-50 p-3">
              <ShieldCheck className="h-5 w-5 shrink-0 text-green-600" aria-hidden="true" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-green-800">{t('production.yunexpress.saved')}</p>
                <p className="text-xs text-green-700/80">{t('production.yunexpress.savedHelp')}</p>
              </div>
              {!isEditingCredentials && (
                <Button
                  ref={editorToggleRef}
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-10 gap-2 border-green-300 bg-white/70 text-green-800 hover:bg-white"
                  onClick={openEditor}
                  aria-controls="yunexpress-credential-editor"
                  aria-expanded={false}
                >
                  <KeyRound className="h-4 w-4" aria-hidden="true" />
                  {t('production.yunexpress.replace')}
                </Button>
              )}
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
              <ShieldAlert className="h-5 w-5 shrink-0 text-amber-600" aria-hidden="true" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-amber-800">{t('production.yunexpress.notSet')}</p>
                <p className="text-xs text-amber-700/80">{t('production.yunexpress.notSetHelp')}</p>
              </div>
              {!isEditingCredentials && (
                <Button
                  ref={editorToggleRef}
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-10 gap-2 border-amber-300 bg-white/70 text-amber-900 hover:bg-white"
                  onClick={openEditor}
                  aria-controls="yunexpress-credential-editor"
                  aria-expanded={false}
                >
                  <KeyRound className="h-4 w-4" aria-hidden="true" />
                  {t('production.yunexpress.add')}
                </Button>
              )}
            </div>
          )}

          {environment && (
            <p className={cn('text-sm', environment.tone)}>{t(environment.key)}</p>
          )}

          {isEditingCredentials && (
            <div id="yunexpress-credential-editor" className="space-y-4 rounded-lg border bg-muted/20 p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium">
                    {hasCredentials ? t('production.yunexpress.replace') : t('production.yunexpress.add')}
                  </p>
                  <p id={editorHintId} className="mt-0.5 text-xs text-muted-foreground">
                    {hasCredentials
                      ? t('production.yunexpress.editorKeepHint')
                      : t('production.yunexpress.editorNewHint')}
                  </p>
                </div>
                <Button type="button" variant="ghost" size="sm" className="h-10 gap-2" onClick={cancelEditor}>
                  <X className="h-4 w-4" aria-hidden="true" />
                  {t('production.common.cancel')}
                </Button>
              </div>

              <div className="space-y-2">
                <Label htmlFor="yunexpress-app-id" className="text-sm font-medium">
                  {t('production.yunexpress.appId')}
                </Label>
                <Input
                  ref={appIdRef}
                  id="yunexpress-app-id"
                  name="yunexpress-app-id-replacement"
                  value={appId}
                  onChange={(e) => setAppId(e.target.value)}
                  className="h-10 max-w-prose font-mono text-sm"
                  {...noAutofillProps}
                  aria-describedby={editorHintId}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="yunexpress-app-secret" className="text-sm font-medium">
                  {t('production.yunexpress.appSecret')}
                </Label>
                <SecretInput
                  id="yunexpress-app-secret"
                  name="yunexpress-app-secret-replacement"
                  value={appSecret}
                  onChange={(e) => setAppSecret(e.target.value)}
                  wrapperClassName="max-w-prose"
                  className="h-10 font-mono text-sm"
                  aria-describedby={editorHintId}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="yunexpress-source-key" className="text-sm font-medium">
                  {t('production.yunexpress.sourceKey')}
                </Label>
                <SecretInput
                  id="yunexpress-source-key"
                  name="yunexpress-source-key-replacement"
                  value={sourceKey}
                  onChange={(e) => setSourceKey(e.target.value)}
                  wrapperClassName="max-w-prose"
                  className="h-10 font-mono text-sm"
                  aria-describedby={editorHintId}
                />
              </div>

              {incompleteFirstSave && (
                <p className="text-xs font-medium text-amber-700" role="status">
                  {t('production.yunexpress.enterAllThree')}
                </p>
              )}
            </div>
          )}

          <div className="flex flex-wrap items-center justify-end gap-3 pt-2">
            {isDirty && hasCredentials && (
              <span id="yunexpress-test-hint" className="text-xs text-muted-foreground">
                {t('production.yunexpress.testSaveFirst')}
              </span>
            )}
            <Button
              variant="outline"
              onClick={handleTest}
              // Test reads the SAVED credentials, so it waits for unsaved edits.
              disabled={!hasCredentials || testMutation.isPending || isDirty}
              aria-describedby={isDirty && hasCredentials ? 'yunexpress-test-hint' : undefined}
              className="h-10 min-w-[120px] gap-2"
            >
              {testMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <Send className="h-4 w-4" aria-hidden="true" />
              )}
              {testMutation.isPending ? t('production.yunexpress.testing') : t('production.yunexpress.test')}
            </Button>
          </div>
        </div>
      )}
    </SettingsSection>
  );
}
